import { ENDPOINTS } from '@/configs/endpoints';
import { TopicEntityApi, TopicEntityListSchema } from '@/entities/Topics';
import { FieldSelectStore, FieldSelectStoreOptions } from '@/stores/local/shared/form/FieldSelectStore';
import { OptionEntity } from '@/types/OptionEntity';
import call from '@/utils/api';
import type { BaseResponse } from '@/utils/api/types';
import { schemeParse } from '@/utils/schemeParse';

type TopicSelectStoreOptions = Omit<FieldSelectStoreOptions<OptionEntity>, 'getOptions'>;

type TopicSelectStoreParams = {
  options?: TopicSelectStoreOptions;
  organizationId: string;
};

type GetOptionsParams = Parameters<FieldSelectStoreOptions<OptionEntity>['getOptions']>[0];

export class TopicSelectStore extends FieldSelectStore<OptionEntity> {
  constructor({ organizationId, options }: TopicSelectStoreParams) {
    super({
      ...options,
      getOptions: async (params: GetOptionsParams): Promise<BaseResponse<{ items: OptionEntity[]; total: number }>> => {
        const { offset, limit, search } = params;
        const response = await this._fetchTopicOptions({ organizationId, offset, limit, search });

        if (response.isError) {
          return {
            isError: true,
            error: response.error,
          };
        }

        const parsed = schemeParse(TopicEntityListSchema, response.data.items);

        if (parsed.error) {
          return {
            isError: false,
            data: {
              items: [],
              total: 0,
            },
          };
        }

        return {
          isError: false,
          data: {
            items: parsed.data.map(({ id, name }) => ({ key: String(id), value: name })),
            total: response.data.total,
          },
        };
      },
    });
  }

  private async _fetchTopicOptions(params: { organizationId: string; offset: number; limit: number; search: string }) {
    return call<{ items: TopicEntityApi[]; total: number }>({
      endpoint: ENDPOINTS.topics.list(params.organizationId),
      data: { ...params },
    });
  }
}
